import clsx from 'clsx';
import { memo, useEffect, useState } from 'react';

import type { AvatarSize } from '../../../types/SharedAvatarProps';
import type { IconGlyphSize } from '../../../types/SharedIconProps';
import type { TextVariant } from '../../../types/TextVariant';

import { Icon } from '../../Icon/Icon';
import { Text } from '../../Text/Text';
import { getAvatarInitials } from '../utils';
import './AvatarChip.css';

export type AvatarChipProps = {
  /** Name for initials and the chip accessible name when visible text differs. */
  name?: string;
  /** Chip diameter. @default 'm' */
  size?: AvatarSize;
  /** Image URL; falls back to initials or user icon on error. */
  src?: string;
  /** EDS-internal only — never set from src/**, only passed sibling-to-sibling within packages/eds. */
  __private?: {
    /** Hides the chip from assistive tech when paired with visible inline text. */
    decorative?: boolean;
  };
};

const AVATAR_CHIP_ICON_SIZE: Record<AvatarSize, IconGlyphSize> = {
  l: 'm',
  m: 's',
  s: 'xs',
};

const AVATAR_CHIP_TEXT_VARIANT: Record<AvatarSize, TextVariant> = {
  l: 'body-semi-xl',
  m: 'body-semi-m',
  s: 'body-semi-s',
};

export const AvatarChip = memo(
  ({ name, size = 'm', src, __private }: AvatarChipProps) => {
    const isDecorative = __private?.decorative === true;
    const resolvedName = name?.trim();
    const initials = getAvatarInitials(name);
    const [imageFailed, setImageFailed] = useState(false);
    const textVariant = AVATAR_CHIP_TEXT_VARIANT[size];
    const iconSize = AVATAR_CHIP_ICON_SIZE[size];

    useEffect(() => {
      setImageFailed(false);
    }, [src]);

    const showImage = Boolean(src) && !imageFailed;
    const chipAccessibleName = resolvedName;
    const needsNamedChip = !isDecorative && Boolean(chipAccessibleName);

    let avatarContent;
    if (showImage && src) {
      avatarContent = (
        <img
          alt=""
          className="eds-avatar--image"
          onError={() => {
            setImageFailed(true);
          }}
          src={src}
        />
      );
    } else if (initials) {
      avatarContent = (
        <Text as="span" variant={textVariant}>
          {initials}
        </Text>
      );
    } else {
      avatarContent = <Icon icon="user" size={iconSize} />;
    }

    return (
      <span
        aria-hidden={isDecorative ? true : undefined}
        aria-label={needsNamedChip ? chipAccessibleName : undefined}
        className={clsx('eds-avatar', `eds-avatar--size-${size}`)}
        role={needsNamedChip ? 'img' : undefined}
      >
        {avatarContent}
      </span>
    );
  },
);

AvatarChip.displayName = 'AvatarChip';
